import { clsx } from "clsx";
import { Info } from "lucide-react";
import { useState } from "react";
import { DatePicker } from "@/components/date-picker";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { dateOnlyToDate, dateToDateOnly } from "@/lib/format.ts";
import { PROJECT_COLORS, PROJECT_TYPES } from "../../data";
import {
	emptyOnboardingAddress,
	isOnboardingAddressComplete,
	type ProjectDraft,
} from "../../types";
import { maskCurrencyBRL } from "../../utils/format";
import type { StructureViolations } from "../../utils/violations";
import { AddressSection } from "../address-section";
import { CompanyNameField } from "../company-name-field";
import { FieldError } from "../field-error";
import { FieldLabel } from "../field-label";
import { StepShell } from "../step-shell";

interface SingleProjectStepProps {
	initialProject: ProjectDraft | null;
	askCompanyName: boolean;
	initialCompanyName: string;
	violations?: StructureViolations;
	onBack: () => void;
	onContinue: (project: ProjectDraft, companyName: string | null) => void;
}

export function SingleProjectStep({
	initialProject,
	askCompanyName,
	initialCompanyName,
	violations,
	onBack,
	onContinue,
}: SingleProjectStepProps) {
	const [companyName, setCompanyName] = useState(initialCompanyName);
	const [name, setName] = useState(initialProject?.name ?? "");
	const [address, setAddress] = useState(
		initialProject?.address ?? emptyOnboardingAddress(),
	);
	const [type, setType] = useState(initialProject?.type ?? "");
	const [budget, setBudget] = useState(initialProject?.budget ?? "");
	const [startDate, setStartDate] = useState(initialProject?.startDate ?? "");
	const [forecastEnd, setForecastEnd] = useState(
		initialProject?.forecastEnd ?? "",
	);
	const [color, setColor] = useState<string>(
		initialProject?.color ?? PROJECT_COLORS[0],
	);

	const canContinue =
		name.trim() !== "" &&
		isOnboardingAddressComplete(address) &&
		type !== "" &&
		(!askCompanyName || companyName.trim() !== "");

	function handleContinue() {
		onContinue(
			{
				id: initialProject?.id ?? crypto.randomUUID(),
				name,
				address,
				type,
				budget,
				startDate,
				forecastEnd,
				color,
			},
			askCompanyName ? companyName.trim() : null,
		);
	}

	return (
		<StepShell
			title="Cadastre sua obra"
			description="Informe os dados da obra que você vai acompanhar."
			onBack={onBack}
			onContinue={handleContinue}
			continueDisabled={!canContinue}
		>
			{askCompanyName && (
				<CompanyNameField
					value={companyName}
					onChange={setCompanyName}
					error={violations?.company_name}
				/>
			)}

			<div className="onboarding-fields">
				<div className="onboarding-field">
					<FieldLabel>Nome da obra</FieldLabel>
					<Input
						placeholder="Ex.: Casa Praia de Tibau"
						value={name}
						aria-invalid={!!violations?.name}
						onChange={(event) => setName(event.target.value)}
					/>
					<FieldError message={violations?.name} />
				</div>
				<AddressSection
					value={address}
					onChange={setAddress}
					violations={violations}
					idPrefix="single-project"
				/>
				<div className="onboarding-field-row">
					<div className="onboarding-field">
						<FieldLabel>Tipologia</FieldLabel>
						<Select value={type} onValueChange={setType}>
							<SelectTrigger
								className="onboarding-select"
								aria-invalid={!!violations?.type}
							>
								<SelectValue placeholder="Selecione a tipologia" />
							</SelectTrigger>
							<SelectContent>
								{PROJECT_TYPES.map((option) => (
									<SelectItem key={option} value={option}>
										{option}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<FieldError message={violations?.type} />
					</div>
					<div className="onboarding-field">
						<FieldLabel>Orçamento (opcional)</FieldLabel>
						<Input
							placeholder="R$ 0"
							inputMode="numeric"
							value={budget}
							onChange={(event) =>
								setBudget(maskCurrencyBRL(event.target.value))
							}
						/>
					</div>
				</div>
				<div className="onboarding-field-row">
					<div className="onboarding-field">
						<FieldLabel>Início previsto</FieldLabel>
						<DatePicker
							selected={dateOnlyToDate(startDate)}
							aria-invalid={!!violations?.startDate}
							onSelected={(date) => setStartDate(dateToDateOnly(date))}
						/>
						<FieldError message={violations?.startDate} />
					</div>
					<div className="onboarding-field">
						<FieldLabel>Entrega prevista</FieldLabel>
						<DatePicker
							selected={dateOnlyToDate(forecastEnd)}
							aria-invalid={!!violations?.forecastEnd}
							onSelected={(date) => setForecastEnd(dateToDateOnly(date))}
						/>
						<FieldError message={violations?.forecastEnd} />
					</div>
				</div>
				<div className="onboarding-field">
					<FieldLabel>Cor identificadora</FieldLabel>
					<div className="color-picker">
						{PROJECT_COLORS.map((value) => (
							<button
								key={value}
								type="button"
								aria-label={`Selecionar cor ${value}`}
								onClick={() => setColor(value)}
								style={{ backgroundColor: value }}
								className={clsx(
									"color-swatch",
									color === value && "is-selected",
								)}
							/>
						))}
					</div>
					<FieldError message={violations?.color} />
				</div>
			</div>

			<div className="onboarding-info">
				<Info className="onboarding-info__icon" />
				<p>
					Criaremos o canteiro automaticamente com este endereço — você
					poderá adicionar mais obras depois.
				</p>
			</div>
		</StepShell>
	);
}
